import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FileText, Plus, Edit2, Trash2, Save, X, ArrowUp, ArrowDown, AlertCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Prompts = () => {
  const { token } = useAuth();
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    content: '',
    category: 'general',
    priority: 1,
    is_active: true
  });

  const categories = [
    { value: 'general', label: 'General' },
    { value: 'reservations', label: 'Reservas' },
    { value: 'wine_info', label: 'Información de Vinos' },
    { value: 'tours', label: 'Tours' },
    { value: 'restaurant', label: 'Restaurant' },
  ];

  useEffect(() => {
    fetchPrompts();
  }, []);

  const fetchPrompts = async () => {
    try {
      const response = await axios.get(`${API}/admin/prompts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setPrompts(response.data);
    } catch (error) {
      console.error('Error fetching prompts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingPrompt) {
        await axios.put(
          `${API}/admin/prompts/${editingPrompt.id}`,
          formData,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
      } else {
        await axios.post(
          `${API}/admin/prompts`,
          formData,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
      }
      
      fetchPrompts();
      resetForm();
    } catch (error) {
      console.error('Error saving prompt:', error);
      alert('Error al guardar el prompt');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este prompt?')) return;
    
    try {
      await axios.delete(`${API}/admin/prompts/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchPrompts();
    } catch (error) {
      console.error('Error deleting prompt:', error);
      alert('Error al eliminar el prompt');
    }
  };

  const handleEdit = (prompt) => {
    setEditingPrompt(prompt);
    setFormData({
      name: prompt.name,
      content: prompt.content,
      category: prompt.category,
      priority: prompt.priority,
      is_active: prompt.is_active
    });
    setShowForm(true);
  };

  const toggleActive = async (id, currentStatus) => {
    try {
      await axios.patch(
        `${API}/admin/prompts/${id}/toggle`,
        {},
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      fetchPrompts();
    } catch (error) {
      console.error('Error toggling prompt:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      content: '',
      category: 'general',
      priority: 1,
      is_active: true
    });
    setEditingPrompt(null);
    setShowForm(false);
  };

  const groupedPrompts = prompts.reduce((acc, prompt) => {
    if (!acc[prompt.category]) {
      acc[prompt.category] = [];
    }
    acc[prompt.category].push(prompt);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-12 h-12 border-4 rounded-full animate-spin" 
             style={{borderColor: '#D4AF37', borderTopColor: 'transparent'}}></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Prompts del Asistente IA</h1>
          <p className="text-gray-600">Configura las instrucciones y comportamiento del asistente</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 px-6 py-3 text-white rounded-lg font-semibold transition-all shadow-lg hover:scale-105"
          style={{background: 'linear-gradient(135deg, #D4AF37 0%, #C9A961 100%)'}}
        >
          <Plus className="h-5 w-5" />
          Nuevo Prompt
        </button>
      </div>

      {/* Info Alert */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-start gap-3">
        <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800">
          <p className="font-semibold mb-1">¿Qué son los prompts?</p>
          <p>Los prompts son instrucciones que guían el comportamiento del asistente IA. Define cómo debe responder a diferentes situaciones y qué información proporcionar.</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
          <p className="text-sm text-gray-600">Total Prompts</p>
          <p className="text-2xl font-bold text-gray-800">{prompts.length}</p>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
          <p className="text-sm text-gray-600">Activos</p>
          <p className="text-2xl font-bold text-green-600">
            {prompts.filter(p => p.is_active).length}
          </p>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
          <p className="text-sm text-gray-600">Inactivos</p>
          <p className="text-2xl font-bold text-gray-600">
            {prompts.filter(p => !p.is_active).length}
          </p>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
          <p className="text-sm text-gray-600">Categorías</p>
          <p className="text-2xl font-bold text-gray-800">
            {Object.keys(groupedPrompts).length}
          </p>
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-800">
                {editingPrompt ? 'Editar Prompt' : 'Nuevo Prompt'}
              </h2>
              <button
                onClick={resetForm}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-6 w-6 text-gray-600" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Name */}
              <div>
                <label className="block text-gray-700 font-medium mb-2">Nombre del Prompt</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                  style={{'--tw-ring-color': '#D4AF37'}}
                  onFocus={(e) => e.target.style.boxShadow = '0 0 0 2px rgba(212, 175, 55, 0.5)'}
                  onBlur={(e) => e.target.style.boxShadow = 'none'}
                  placeholder="ej: Saludo Inicial, Info de Reservas..."
                />
              </div>

              {/* Category & Priority */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">Categoría</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                    style={{'--tw-ring-color': '#D4AF37'}}
                  >
                    {categories.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-gray-700 font-medium mb-2">Prioridad</label>
                  <input
                    type="number"
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })}
                    min="1"
                    max="10"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                    style={{'--tw-ring-color': '#D4AF37'}}
                  />
                </div>
              </div>

              {/* Content */}
              <div>
                <label className="block text-gray-700 font-medium mb-2">Contenido del Prompt</label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  required
                  rows={10}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent resize-none font-mono text-sm"
                  style={{'--tw-ring-color': '#D4AF37'}}
                  onFocus={(e) => e.target.style.boxShadow = '0 0 0 2px rgba(212, 175, 55, 0.5)'}
                  onBlur={(e) => e.target.style.boxShadow = 'none'}
                  placeholder="Escribe las instrucciones detalladas para el asistente..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  Sé específico y claro. El asistente seguirá estas instrucciones en sus respuestas.
                </p>
              </div>

              {/* Active Toggle */}
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-5 h-5 rounded"
                  style={{accentColor: '#D4AF37'}}
                />
                <label htmlFor="is_active" className="text-gray-700 font-medium">
                  Prompt activo
                </label>
              </div>

              {/* Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3 text-white rounded-lg font-semibold transition-all"
                  style={{background: 'linear-gradient(135deg, #D4AF37 0%, #C9A961 100%)'}}
                >
                  <Save className="h-5 w-5" />
                  {editingPrompt ? 'Actualizar' : 'Crear'}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Prompts List by Category */}
      <div className="space-y-6">
        {Object.entries(groupedPrompts).map(([category, categoryPrompts]) => (
          <div key={category} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <h3 className="font-bold text-lg text-gray-800">
                {categories.find(c => c.value === category)?.label || category}
              </h3>
              <p className="text-sm text-gray-600">{categoryPrompts.length} prompt(s)</p>
            </div>
            <div className="divide-y divide-gray-200">
              {categoryPrompts.sort((a, b) => b.priority - a.priority).map((prompt) => (
                <div key={prompt.id} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="font-bold text-gray-800">{prompt.name}</h4>
                        <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full font-medium">
                          Prioridad: {prompt.priority}
                        </span>
                        <button
                          onClick={() => toggleActive(prompt.id, prompt.is_active)}
                          className={`px-3 py-1 text-xs rounded-full font-medium transition-colors ${
                            prompt.is_active
                              ? 'bg-green-100 text-green-700 hover:bg-green-200'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          {prompt.is_active ? '✓ Activo' : '✗ Inactivo'}
                        </button>
                      </div>
                      <p className="text-sm text-gray-600 whitespace-pre-wrap">{prompt.content}</p>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <button
                        onClick={() => handleEdit(prompt)}
                        className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                        title="Editar"
                      >
                        <Edit2 className="h-4 w-4 text-gray-600" />
                      </button>
                      <button
                        onClick={() => handleDelete(prompt.id)}
                        className="p-2 hover:bg-red-100 rounded-lg transition-colors"
                        title="Eliminar"
                      >
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </button>
                    </div>
                  </div>
                  <p className="text-xs text-gray-400">
                    Última actualización: {new Date(prompt.updated_at).toLocaleString('es-ES')}
                  </p>
                </div>
              ))}
            </div>
          </div>
        ))}

        {prompts.length === 0 && (
          <div className="text-center py-12 bg-white rounded-xl shadow-sm border border-gray-200">
            <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-800 mb-2">No hay prompts configurados</h3>
            <p className="text-gray-600 mb-4">Crea tu primer prompt para comenzar</p>
            <button
              onClick={() => setShowForm(true)}
              className="inline-flex items-center gap-2 px-6 py-3 text-white rounded-lg font-semibold transition-all shadow-lg hover:scale-105"
              style={{background: 'linear-gradient(135deg, #D4AF37 0%, #C9A961 100%)'}}
            >
              <Plus className="h-5 w-5" />
              Nuevo Prompt
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Prompts;
